import PermissionButton from '../../permissions/PermissionButton';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import dayjs from 'dayjs';
import {
  Card,
  Form,
  Select,
  DatePicker,
  InputNumber,
  Switch,
  Alert,
  Space,
  Typography,
  Divider,
  Tag,
  Table,
  message,
  Tooltip,
  Progress,
  Spin
} from 'antd';
import { SyncOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import archiveService from '../../services/archiveService';

const { Text } = Typography;

// Polling interval in ms
const POLL_INTERVAL = 2000;

const ArchiveManager = () => {
  const [modelOptions, setModelOptions] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [selectedModels, setSelectedModels] = useState([]);
  const [archiveAll, setArchiveAll] = useState(false);
  const [cutoffDate, setCutoffDate] = useState(null);
  const [yearsBack, setYearsBack] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  // Job tracking state
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const pollIntervalRef = useRef(null);

  // Load models on mount
  useEffect(() => {
    const loadModels = async () => {
      setModelsLoading(true);
      try {
        const response = await archiveService.fetchArchiveModels();
        setModelOptions(response.models || []);
      } catch (err) {
        console.error('Failed to load archive models:', err);
        message.error('Failed to load archive models');
      } finally {
        setModelsLoading(false);
      }
    };

    loadModels();
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Poll for job status
  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const status = await archiveService.getArchiveStatus(jobId);
      setJobStatus(status);

      // Check if job is complete
      if (['done', 'done_with_errors', 'failed'].includes(status.status)) {
        // Stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setLoading(false);

        // Build summary from job results
        if (status.status !== 'failed') {
          setSummary({
            startTime: status.startedAt,
            endTime: status.completedAt,
            durationSeconds: status.durationSeconds,
            cutoffDate: status.cutoffDate,
            dryRun: status.dryRun,
            models: status.progress?.processedModels || [],
            all: status.params?.all,
            results: status.results,
            totalArchived: status.totalArchived,
            successCount: status.successCount,
            failureCount: status.failureCount
          });

          if (status.status === 'done') {
            message.success(status.dryRun ? 'Dry run completed. Review the summary below.' : 'Archive completed successfully.');
          } else if (status.status === 'done_with_errors') {
            message.warning('Archive completed with some errors. Check the summary.');
          }
        } else {
          setError(status.error || 'Archive job failed');
          message.error(status.error || 'Archive job failed');
        }
      }
    } catch (err) {
      console.error('Failed to poll job status:', err);
      // Don't stop polling on network errors, just log
      if (err.response?.status === 404) {
        // Job not found, stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setLoading(false);
        setError('Job not found. It may have expired.');
      }
    }
  }, []);

  const handleRunArchive = async () => {
    if (!archiveAll && selectedModels.length === 0) {
      message.warning('Select at least one model or enable "Archive all models".');
      return;
    }

    const payload = {
      models: selectedModels,
      all: archiveAll,
      dryRun,
      cutoffDate: cutoffDate ? cutoffDate.toISOString() : null,
      yearsBack: yearsBack || null
    };

    setLoading(true);
    setError(null);
    setSummary(null);
    setJobStatus(null);

    try {
      // Start the archive job (fire-and-forget)
      const response = await archiveService.startArchive(payload);

      if (!response.success) {
        throw new Error(response.message || 'Failed to start archive');
      }

      const jobId = response.jobId;
      setCurrentJobId(jobId);
      message.info('Archive job started. Tracking progress...');

      // Start polling for status
      pollIntervalRef.current = setInterval(() => {
        pollJobStatus(jobId);
      }, POLL_INTERVAL);

      // Also poll immediately
      pollJobStatus(jobId);

    } catch (err) {
      setLoading(false);
      const errorMessage = err.response?.data?.message || err.message || 'Archive request failed';
      
      // Check if there's an existing job
      if (err.response?.status === 409) {
        const existingJobId = err.response?.data?.existingJobId;
        if (existingJobId) {
          setCurrentJobId(existingJobId);
          message.warning('An archive is already in progress. Tracking existing job...');
          
          // Start polling existing job
          pollIntervalRef.current = setInterval(() => {
            pollJobStatus(existingJobId);
          }, POLL_INTERVAL);
          pollJobStatus(existingJobId);
          return;
        }
      }
      
      setError(errorMessage);
      message.error(errorMessage);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'queued':
        return <SyncOutlined spin style={{ color: '#1890ff' }} />;
      case 'running':
        return <SyncOutlined spin style={{ color: '#52c41a' }} />;
      case 'done':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'done_with_errors':
        return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'queued': return 'processing';
      case 'running': return 'success';
      case 'done': return 'success';
      case 'done_with_errors': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const summaryColumns = [
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <Tag color={status === 'success' ? 'green' : status === 'preview' ? 'blue' : 'red'}>
          {status === 'preview' ? 'DRY RUN' : status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Records',
      dataIndex: 'count',
      key: 'count'
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message'
    }
  ];

  const summaryData = summary
    ? Object.entries(summary.results || {}).map(([model, details]) => ({
        key: model,
        model,
        status: details.success ? (details.dryRun ? 'preview' : 'success') : 'error',
        count: details.count || 0,
        message: details.error || (details.dryRun ? 'Preview only' : 'Archived successfully')
      }))
    : [];

  // Calculate progress percentage
  const progressPercent = jobStatus?.progress
    ? Math.round((jobStatus.progress.processedModels.length / jobStatus.progress.totalModels) * 100)
    : 0;

  return (
    <div>
      <Card title="Archive Hot Records">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Archive Instructions"
            description={
              <div>
                <p>
                  Select the models you want to archive and choose a cutoff date. Records with dates{' '}
                  <strong>before the cutoff date</strong> will transition from hot to cold.
                </p>
                <p>
                  Use the <strong>Dry Run</strong> mode to preview how many records would be archived
                  before running the live archive.
                </p>
              </div>
            }
          />

          {error && <Alert type="error" showIcon message={error} />}

          {/* Job Progress Section */}
          {loading && jobStatus && (
            <Card size="small" title={<Space>{getStatusIcon(jobStatus.status)} Archive Progress</Space>}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Space>
                  <Text strong>Job ID:</Text>
                  <Text code>{currentJobId}</Text>
                  <Tag color={getStatusColor(jobStatus.status)}>
                    {jobStatus.status?.toUpperCase()}
                  </Tag>
                </Space>
                
                <Progress 
                  percent={progressPercent} 
                  status={jobStatus.status === 'running' ? 'active' : 'normal'}
                />
                
                {jobStatus.progress?.currentModel && (
                  <Space>
                    <Spin size="small" />
                    <Text>Processing: <Text strong>{jobStatus.progress.currentModel}</Text></Text>
                  </Space>
                )}
                
                <Text type="secondary">
                  Processed {jobStatus.progress?.processedModels?.length || 0} of {jobStatus.progress?.totalModels || 0} models
                </Text>
                
                {jobStatus.totalArchived > 0 && (
                  <Text>
                    Records {summary?.dryRun ? 'to archive' : 'archived'}: <Text strong>{jobStatus.totalArchived}</Text>
                  </Text>
                )}
              </Space>
            </Card>
          )}

          <Form layout="vertical">
            <Form.Item label="Archive scope">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Switch
                  checkedChildren="All models"
                  unCheckedChildren="Select models"
                  checked={archiveAll}
                  disabled={loading}
                  onChange={checked => {
                    setArchiveAll(checked);
                    if (checked) {
                      setSelectedModels([]);
                    }
                  }}
                />
                <Text type="secondary">
                  {archiveAll
                    ? 'All supported models will be archived.'
                    : 'Select specific models to include in this archive run.'}
                </Text>
              </Space>
            </Form.Item>

            <Form.Item label="Models" required={!archiveAll}>
              <Select
                mode="multiple"
                disabled={archiveAll || loading}
                value={selectedModels}
                onChange={setSelectedModels}
                placeholder={archiveAll ? 'Disabled when archiving all models' : 'Select one or more models'}
                loading={modelsLoading}
                options={modelOptions.map(model => ({
                  label: `${model.displayName} (${model.name})`,
                  value: model.name
                }))}
              />
            </Form.Item>

            <Form.Item label="Cutoff date">
              <DatePicker
                style={{ width: '100%' }}
                value={cutoffDate}
                onChange={value => setCutoffDate(value)}
                disabled={loading}
                allowClear
                placeholder="Select cutoff date (defaults to Jan 1 of current year)"
              />
            </Form.Item>

            <Form.Item
              label={
                <Space>
                  <span>Archive records older than N years</span>
                  <Tooltip title="Optional shortcut that sets cutoff date to January 1st of the year calculated by (current year - N).">
                    <Tag color="blue">Help</Tag>
                  </Tooltip>
                </Space>
              }
            >
              <InputNumber
                min={1}
                value={yearsBack}
                onChange={setYearsBack}
                disabled={loading}
                style={{ width: '100%' }}
                placeholder="Leave empty to use cutoff date"
              />
            </Form.Item>

            <Form.Item label="Dry run">
              <Switch
                checkedChildren="Dry run"
                unCheckedChildren="Live"
                checked={dryRun}
                disabled={loading}
                onChange={setDryRun}
              />
              <Text type="secondary" style={{ marginLeft: 8 }}>
                {dryRun
                  ? 'Preview only (no data changes).'
                  : 'Live mode (records will be archived).'}
              </Text>
            </Form.Item>

            <Divider />

            <PermissionButton permissionModule="archives" permissionAction="run" type="primary" onClick={handleRunArchive} loading={loading}>
              {loading ? 'Processing...' : (dryRun ? 'Run Dry Run' : 'Run Archive')}
            </PermissionButton>
          </Form>
        </Space>
      </Card>

      {summary && (
        <Card title="Archive Summary" style={{ marginTop: 24 }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space wrap>
              <Tag color="default">
                Models: {summary.models?.length || 0} {summary.all ? '(all)' : ''}
              </Tag>
              <Tag color="purple">
                Cutoff: {dayjs(summary.cutoffDate).format('YYYY-MM-DD')}
              </Tag>
              <Tag color={summary.dryRun ? 'blue' : 'green'}>
                Mode: {summary.dryRun ? 'Dry Run' : 'Live'}
              </Tag>
              <Tag color="geekblue">Duration: {summary.durationSeconds?.toFixed(2)}s</Tag>
              <Tag color="cyan">Total: {summary.totalArchived}</Tag>
              {summary.failureCount > 0 && (
                <Tag color="error">Failures: {summary.failureCount}</Tag>
              )}
            </Space>

            <Table
              dataSource={summaryData}
              columns={summaryColumns}
              pagination={false}
              rowKey="key"
            />
          </Space>
        </Card>
      )}
    </div>
  );
};

export default ArchiveManager;
